import { Icon, JumpList, Row } from '@advanza/advanza_generic'
import Pagination from 'components/Pagination'
import SafeButtonBase from 'components/ui/SafeButton'
import ReviewFilterContainer from 'containers/ReviewFilterContainer'
import React from 'react'

/** SafeButton accepts string confirmMessage/buttonText at runtime; cast for TS. */
const SafeButton = SafeButtonBase as unknown as React.ComponentType<{
    action: () => Promise<unknown>
    className?: string
    confirmMessage?: string
    buttonText?: string
}>

interface ReviewFilterFilter {
    filterId: string
    page?: number
    pageSize?: number
    searchKey?: string
    selection?: (string | number)[]
    didInvalidate?: boolean
    [key: string]: unknown
}

export interface ReviewFilterProps {
    filterId: string
    filter?: ReviewFilterFilter
    count?: number
    isFetching?: boolean
    compact?: boolean
    defaultOptions?: Record<string, unknown>
    newEntityOptions?: Record<string, unknown>
    changeFilter: (id: string, options: Record<string, unknown>) => void
    addReview: (options: Record<string, unknown>, filterId: string) => void
    deleteSelection: (filterId: string) => Promise<unknown>
}

class ReviewFilter extends React.Component<ReviewFilterProps> {
    _changeFilter = (options: Record<string, unknown>): void => {
        const { changeFilter, filterId } = this.props
        changeFilter(filterId, options)
    }

    componentDidMount(): void {
        const { defaultOptions = {}, filter } = this.props
        if (!filter) {
            this._changeFilter(defaultOptions)
        }
    }

    componentDidUpdate(prevProps: ReviewFilterProps): void {
        const prevFilter = (prevProps.filter ?? {}) as ReviewFilterFilter
        const filter = (this.props.filter ?? {}) as ReviewFilterFilter
        const didInvalidate = !prevFilter.didInvalidate && filter.didInvalidate
        if (didInvalidate) {
            this._changeFilter({})
        }
    }

    onPageChange = (page: number, invalidate = false): void => {
        this._changeFilter({ page, didInvalidate: invalidate })
    }

    deleteSelection = (): Promise<unknown> => {
        const { filter, deleteSelection } = this.props
        return deleteSelection(filter!.filterId)
    }

    addReview = (): void => {
        const { filter, newEntityOptions, addReview } = this.props
        addReview(newEntityOptions ?? {}, filter!.filterId)
        this._changeFilter({ page: 0 })
    }

    render(): React.ReactNode {
        const { filter, count, compact, newEntityOptions } = this.props
        if (!filter || compact) {
            return null
        }

        const hasSelection = filter.selection !== undefined && filter.selection.length > 0
        const pageNr = filter.page ?? 0

        return (
            <Row className="v-center" nm>
                {newEntityOptions !== undefined && (
                    <button type="button" className="plain" onClick={this.addReview}>
                        <span className="inline-flex items-center gap-1 pt-2">
                            <Icon>add</Icon> Add review
                        </span>
                    </button>
                )}
                {hasSelection && (
                    <JumpList
                        items={[
                            {
                                _element: (
                                    <SafeButton
                                        action={this.deleteSelection}
                                        className="btn"
                                        confirmMessage={`Delete ${filter.selection!.length} reviews`}
                                        buttonText="Delete selection"
                                    />
                                ),
                            },
                        ]}
                    />
                )}
                {count !== undefined &&
                    filter.pageSize !== undefined &&
                    count > filter.pageSize && (
                        <div className="to-right">
                            <Pagination
                                current={pageNr}
                                pageSize={filter.pageSize}
                                count={count}
                                onPageChange={this.onPageChange}
                            />
                        </div>
                    )}
            </Row>
        )
    }
}

export default ReviewFilterContainer(ReviewFilter)
